import type { PollResultCard } from '@dmp/qqms/types';
import type {
    CardState,
    DynamicCoreQuestionState,
    LinearQuestionState,
    QuestionState,
    StateGetter,
    StateSetter,
} from '../../state-types';
import { pathOr } from '../utils/path-or';
import { buildAddCardResult } from './add-card';
import { checkCardHasPollResult } from './check-card-has-poll-result';
import { checkCardIsConnected } from './check-card-is-connected';
import { deleteCard } from './delete-card';
import { getCardComputedResultCardId } from './get-card-computed-result-card-id';
import { getCardNextIndexCardId } from './get-card-next-index-card-id';
import { getCardVisualType } from './get-card-visual-type';
import { moveCard } from './move-card';
import { switchCardType } from './switch-card-type';
import { updateCard } from './update-card';
import { updateCardMaximizeResponses } from './update-card-maximize-responses';

export const cardsActions = (set: StateSetter, get: StateGetter) => {
    return {
        /** Get a Card State by id. Should be used with `useShallow` in React components */
        getCard: (cardId: string): CardState | undefined => get().cards[cardId],

        /** Get the form type of the Question Card */
        getCardFormType: (cardId: string) =>
            pathOr<QuestionState['formType']>(
                get(),
                ['cards', cardId, 'formType'],
                'radio'
            ),

        /** Get the type of the Card, falling back to `linearQuestion` */
        getCardType: (cardId: string) =>
            pathOr<CardState['type']>(
                get(),
                ['cards', cardId, 'type'],
                'linearQuestion'
            ),

        /** Get the button copy of the Title Card */
        getCardButtonCopy: (cardId: string) =>
            pathOr<string>(get(), ['cards', cardId, 'buttonCopy'], ''),

        /** Get the Traxex Label of the Card */
        getCardTraxexLabel: (cardId: string) =>
            pathOr<string>(get(), ['cards', cardId, 'traxexLabel'], ''),

        /** Get the body of the Question Card */
        getCardBody: (cardId: string) =>
            pathOr<string>(get(), ['cards', cardId, 'body'], ''),

        /** Get the `isSkippable` of the Linear Question */
        getCardIsSkippable: (cardId: string) =>
            pathOr<LinearQuestionState['isSkippable']>(
                get(),
                ['cards', cardId, 'isSkippable'],
                undefined
            ),

        /** Get the `shuffleAnswers` of the Question */
        getCardShuffleAnswers: (cardId: string) =>
            pathOr<QuestionState['shuffleAnswers']>(
                get(),
                ['cards', cardId, 'shuffleAnswers'],
                undefined
            ),

        /** Get the `layout` of the Question Card */
        getCardLayout: (cardId: string) =>
            pathOr<QuestionState['layout']>(
                get(),
                ['cards', cardId, 'layout'],
                'grid'
            ),

        /** Get the `layout` of the Question Card */
        getPollResultLayout: (cardId: string) =>
            pathOr<PollResultCard['layout']>(
                get(),
                ['cards', cardId, 'layout'],
                'filled'
            ),

        /** Get a specific property of a Poll Result Card, with a fallback value. */
        getPollResultProperty: <T extends keyof PollResultCard>(
            cardId: string,
            key: T,
            fallbackValue: PollResultCard[T]
        ) =>
            pathOr<PollResultCard[T]>(
                get(),
                ['cards', cardId, key],
                fallbackValue
            ),

        /** Get the question id of the Dynamic Question Card */
        getCardQuestionIds: (cardId: string) =>
            pathOr<string[]>(get(), ['cards', cardId, 'questionIds'], []),

        /** Get the question id of the Poll Result Card */
        getCardQuestionId: (cardId: string) =>
            pathOr<string>(get(), ['cards', cardId, 'questionId'], ''),

        /** Get Dynamic Questions's `allowReask`, falling back to `false` */
        getCardAllowReask: (cardId: string) =>
            pathOr<boolean>(get(), ['cards', cardId, 'allowReask'], false),

        /** Get `maximizeResponses` of the Dynamic Core Question */
        getCardMaximizeResponses: (cardId: string) =>
            pathOr<DynamicCoreQuestionState['maximizeResponses']>(
                get(),
                ['cards', cardId, 'maximizeResponses'],
                undefined
            ),

        /** Get card that has `resultCard` */
        getCardResultCard: (cardId: string) =>
            pathOr<string | undefined>(
                get(),
                ['cards', cardId, 'resultCard'],
                undefined
            ),

        /** Get computed result card id, fallback to the next card id */
        getCardComputedResultCardId: (cardId: string) =>
            getCardComputedResultCardId(cardId)(get()),

        /** Get sibling ids of the given card id */
        getCardSiblingIds: (cardId: string): string[] => {
            const cardIds = get().survey.cardIds;
            return cardIds.filter((id) => id !== cardId);
        },

        /** Get next index card id */
        getCardNextIndexCardId: (cardId: string) =>
            getCardNextIndexCardId(cardId)(get()),

        /** Get card visual type */
        getCardVisualType: (cardId: string) => getCardVisualType(cardId)(get()),

        /** Check if the card exists in the survey */
        checkCardExists: (cardId: string) =>
            pathOr<CardState['type'] | undefined>(
                get(),
                ['cards', cardId, 'type'],
                undefined
            ) !== undefined,

        /** Check if the card is connected to the other cards in the survey */
        checkCardIsConnected: (cardId: string) =>
            checkCardIsConnected(cardId)(get()),

        /** Check card if it has (connection with) Poll Result */
        checkCardHasPollResult: (cardId: string) =>
            checkCardHasPollResult(cardId)(get()),

        /** Add a new card into they survey.
         * - if `insertAfterCardId` is given, the new card will be inserted after that card
         */
        addCard: (
            cardType: CardState['type'],
            insertAfterCardId: string = '',
            partialCard: Partial<CardState> = {}
        ) => {
            const { nextState, newCardId } = buildAddCardResult(
                get(),
                cardType,
                insertAfterCardId,
                partialCard
            );

            set(nextState, true);
            return newCardId;
        },

        /** Update a card partially */
        updateCard: (cardId: string, partialCard: Partial<CardState>) =>
            set(updateCard(cardId, partialCard), true),

        /** Update a Dynamic Core Question card `maximizeResponses` and conditionally includes all core questions when the value is true */
        updateCardMaximizeResponses: (
            cardId: string,
            maximizeResponses: DynamicCoreQuestionState['maximizeResponses']
        ) => set(updateCardMaximizeResponses(cardId, maximizeResponses), true),

        /** Delete card, including its related answers and connections */
        deleteCard: (removeCardId: string) =>
            set(deleteCard(get(), removeCardId), true),

        /** Move a card into a new index */
        moveCard: (cardId: string, newIndex: number) =>
            set(moveCard(cardId, newIndex), true),

        /** Switch card type. Currently only support Question card. */
        switchCardType: (
            cardId: string,
            type: CardState['type'],
            isMultiSelect: boolean = false
        ) => set(switchCardType(cardId, type, isMultiSelect), true),
    };
};
